import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import { TEAL, AMBER, RED, BORD, CARD, SUNK, MUTED, DIM, TEXT, kindOf, qty, perOne, perOneTyped } from '../stockShared';

// What one of an item is made from -- the BOM, stage by stage (the director,
// 23 Oct 2026): Nastavljeni 03 takes 1 × Nastavljeni 02 and 1 × Spojka Mala;
// the packed product its last stage, its folding box and 1/30 of a transport
// box. Shown, and changed here; recording Made then takes these off stock.
// For a finished product, what MouldOS says it takes of its moulded parts is
// offered as a start.
//
// A moulded item's list is what is moulded into it (26 Oct 2026: a Sito round
// its mesh): production takes those off for every good piece.
//
// A finished product's packing -- boxes, pallet, tape, film, cover sheet --
// is worked out from Admin → Packaging (27 Oct 2026), shown here, never typed:
// "make the app smart". Packing typed into its list by hand is counted no more.
//
// item: the item (with .inputs)   items: every item, to pick inputs from
// hints: MouldOS's parts for a finished product   onSaved(): after saving

// A small amount as people say it: 0.53 g, not 0.00053 kg; 9 cm, not 0.09 m.
function amount(q, unit) {
  const n = Number(q);
  const u = String(unit || '').toLowerCase();
  const r = (x) => Math.round(x * 100) / 100;
  if (u === 'kg' && n < 1) return `${r(n * 1000)} g`;
  if (u === 'm' && n < 1) return `${r(n * 100)} cm`;
  if (u === 'kg' || u === 'm') return `${r(n)} ${u}`;
  return perOne(n);
}

export default function StockMadeFrom({ item, items, hints = [], onSaved, canEdit = true }) {
  const moulded = item.kind === 'moulded';
  const [editing, setEditing] = useState(false);
  const [rows, setRows] = useState([]);          // [{ input_id, qty (as typed) }]
  const [find, setFind] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const byId = useMemo(() => new Map(items.map(i => [i.id, i])), [items]);
  const inputs = item.inputs || [];

  function start() {
    setRows(inputs.map(e => ({ input_id: e.input_id, qty: perOneTyped(e.qty) })));
    setFind(''); setError(null); setEditing(true);
  }
  const add = (id, q = '1') => setRows(r => (r.some(x => x.input_id === id) ? r : [...r, { input_id: id, qty: q }]));
  const found = useMemo(() => {
    const s = find.trim().toLowerCase();
    if (!s) return [];
    return items.filter(i => i.id !== item.id && i.active !== false && !rows.some(r => r.input_id === i.id)
      && (i.name.toLowerCase().includes(s) || (i.link?.name || '').toLowerCase().includes(s))).slice(0, 12);
  }, [find, items, item.id, rows]);

  async function save() {
    setSaving(true); setError(null);
    try {
      await api.put(`/api/v1/stock/items/${item.id}/inputs`, { inputs: rows });
      setEditing(false); setSaving(false);
      onSaved?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Not saved. Check the connection and try again.');
      setSaving(false);
    }
  }

  const field = { background: SUNK, border: `1px solid ${BORD}`, borderRadius: 8, color: TEXT, padding: '8px 10px', fontSize: 14, outline: 'none', fontFamily: 'system-ui', boxSizing: 'border-box' };
  const btn = (primary) => ({ background: primary ? TEAL : 'transparent', color: primary ? '#0f172a' : TEAL, border: `1px solid ${primary ? TEAL : `${TEAL}88`}`,
    borderRadius: 9, padding: '7px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'system-ui', whiteSpace: 'nowrap' });
  const unmatched = hints.filter(h => !h.items.length);

  return (
    <section style={{ marginTop: 22 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
        <h2 style={{ fontSize: 15, fontWeight: 800, margin: 0 }}>{moulded ? 'Moulded in' : 'Made from'}</h2>
        <span style={{ fontSize: 12.5, color: MUTED }}>for one {item.name}</span>
        <span style={{ flex: 1 }} />
        {!editing && canEdit && <button type="button" onClick={start} style={btn(false)}>
          {moulded ? (inputs.length ? 'Change the inserts' : 'Set what is moulded in') : inputs.length ? 'Change what it takes' : 'Set what it is made from'}
        </button>}
      </div>

      {!editing && !inputs.length && (
        <div style={{ fontSize: 13, color: MUTED, lineHeight: 1.6 }}>
          Nothing set. {!canEdit ? 'A director sets it in Stock.' : item.kind === 'stage' || item.kind === 'finished'
            ? 'Set it, and Made takes the parts off stock, and the list shows how many more could be made.'
            : moulded
              ? 'If something goes into the mould with it, like the mesh in a Sito, set it here: every good piece production records then takes one off stock.'
              : 'Bought-in items usually are not made from other stock items.'}
        </div>
      )}

      {!editing && inputs.length > 0 && moulded && (
        <div style={{ fontSize: 12.5, color: DIM, marginBottom: 8, lineHeight: 1.5 }}>
          Every good piece production records takes these off stock. A product it goes into counts only the {item.name} on the shelf.
        </div>
      )}

      {!editing && inputs.length > 0 && (
        <div style={{ background: CARD, border: `1px solid ${BORD}`, borderRadius: 12, overflow: 'hidden' }}>
          {inputs.map((e, n) => {
            const k = kindOf(e.kind);
            return (
              <div key={e.input_id} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '9px 14px', borderTop: n ? `1px solid ${BORD}` : 'none', opacity: e.replaced ? 0.55 : 1 }}>
                <span style={{ fontWeight: 800, minWidth: 64, fontVariantNumeric: 'tabular-nums', color: TEAL, textDecoration: e.replaced ? 'line-through' : 'none' }}>{perOne(e.qty)} ×</span>
                <Link to={`/stock/${e.input_id}`} style={{ flex: 1, minWidth: 0, color: TEXT, textDecoration: 'none', fontWeight: 600 }}>
                  {e.name} <span style={{ fontSize: 11.5, color: k.color, fontWeight: 400, marginLeft: 6 }}>{k.label}</span>
                  {e.replaced && <span style={{ fontSize: 11.5, color: AMBER, fontWeight: 400, marginLeft: 6 }}>Packaging counts this now, below</span>}
                </Link>
                <span style={{ fontSize: 12.5, color: e.level?.counted ? DIM : AMBER, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                  {e.level?.counted ? `${qty(e.level.total, e)} on hand` : 'not counted yet'}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <div style={{ background: CARD, border: `1px solid ${TEAL}66`, borderRadius: 12, padding: 12 }}>
          <div style={{ fontSize: 12.5, color: DIM, marginBottom: 10, lineHeight: 1.5 }}>
            {moulded
              ? <>How many of each insert go into the mould for one {item.name}. One mesh shared by two: type <b style={{ color: TEXT }}>1/2</b>.</>
              : <>How many of each one {item.name} takes. A box for every 30: type <b style={{ color: TEXT }}>1/30</b>. In stages, list the stage below it and what this stage adds, not every part from the start.</>}
          </div>
          {rows.map((r, n) => {
            const it = byId.get(r.input_id);
            return (
              <div key={r.input_id} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                <input value={r.qty} onChange={e => setRows(x => x.map((y, m) => (m === n ? { ...y, qty: e.target.value } : y)))} inputMode="decimal"
                  aria-label={`How many ${it?.name || ''}`} style={{ ...field, width: 74, fontWeight: 800, textAlign: 'right' }} />
                <span style={{ color: MUTED }}>×</span>
                <span style={{ flex: 1, minWidth: 0, fontSize: 14 }}>{it?.name || 'An item'}</span>
                <button type="button" onClick={() => setRows(x => x.filter((_, m) => m !== n))} aria-label={`Take ${it?.name || 'it'} off`}
                  style={{ background: 'none', border: 'none', color: MUTED, cursor: 'pointer', fontSize: 16, padding: '4px 8px' }}>✕</button>
              </div>
            );
          })}
          <input value={find} onChange={e => setFind(e.target.value)} placeholder="Add: find an item (Nastavljeni, Spojka, Zlozenke…)" aria-label="Find an item to add"
            style={{ ...field, width: '100%', marginTop: 4 }} />
          {found.length > 0 && (
            <div style={{ border: `1px solid ${BORD}`, borderRadius: 8, marginTop: 4, maxHeight: 220, overflowY: 'auto' }}>
              {found.map(i => (
                <button key={i.id} type="button" onClick={() => { add(i.id); setFind(''); }}
                  style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', borderTop: `1px solid ${BORD}`, color: TEXT, padding: '8px 10px', cursor: 'pointer', fontFamily: 'system-ui', fontSize: 13.5 }}>
                  {i.name} <span style={{ fontSize: 11.5, color: kindOf(i.kind).color, marginLeft: 6 }}>{kindOf(i.kind).label}</span>
                </button>
              ))}
            </div>
          )}

          {hints.length > 0 && (
            <div style={{ marginTop: 12, fontSize: 12.5, color: DIM, lineHeight: 1.6 }}>
              <div style={{ fontWeight: 700, color: TEXT, marginBottom: 2 }}>MouldOS says one {item.name} takes:</div>
              {hints.map((h, n) => (
                <div key={n}>
                  {h.qty ?? '?'} × {h.part}
                  {h.items.length > 0 && <> → {h.items.map((x, m) => (
                    <span key={x.id}>{m ? ', ' : ''}
                      <button type="button" onClick={() => add(x.id, String(h.qty || 1))} disabled={rows.some(r => r.input_id === x.id)}
                        style={{ background: 'none', border: 'none', padding: 0, color: rows.some(r => r.input_id === x.id) ? MUTED : TEAL, cursor: 'pointer', fontFamily: 'system-ui', fontSize: 12.5 }}>
                        {rows.some(r => r.input_id === x.id) ? `${x.name} (added)` : `+ ${x.name}`}
                      </button>
                    </span>
                  ))}</>}
                </div>
              ))}
              {unmatched.length > 0 && <div style={{ color: MUTED }}>Not on the stock list: {unmatched.map(h => h.part).join(', ')}.</div>}
              {hints.some(h => h.items.length > 1) && <div style={{ color: MUTED }}>Where one mould makes several items, add each one it takes.</div>}
            </div>
          )}

          {error && <div style={{ color: RED, fontSize: 13, marginTop: 10, lineHeight: 1.5 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
            <button type="button" onClick={() => setEditing(false)} style={{ ...btn(false), color: DIM, borderColor: '#374151' }}>Cancel</button>
            <button type="button" onClick={save} disabled={saving} style={btn(true)}>{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
      )}

      {item.packing && (
        <div style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
            <h3 style={{ fontSize: 14, fontWeight: 800, margin: 0 }}>Packing</h3>
            <span style={{ fontSize: 12.5, color: MUTED }}>worked out from how {item.packing.product} is packed</span>
            <span style={{ flex: 1 }} />
            {canEdit && <Link to={`/admin/packaging?tab=items${(item.product_id || (item.products || []).length === 1) ? `&product=${item.product_id || item.products[0].id}` : ''}`} style={{ ...btn(false), textDecoration: 'none' }}>Change in Packaging</Link>}
          </div>
          {item.packing.lines.length > 0 && (
            <div style={{ background: CARD, border: `1px solid ${BORD}`, borderRadius: 12, overflow: 'hidden' }}>
              {item.packing.lines.map((l, n) => (
                <div key={`${l.container_id}-${n}`} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '9px 14px', borderTop: n ? `1px solid ${BORD}` : 'none', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 800, minWidth: 64, fontVariantNumeric: 'tabular-nums', color: l.qty != null ? TEAL : AMBER }}>{l.qty != null ? `${amount(l.qty, l.unit)}${String(l.unit || '').toLowerCase() === 'kg' || String(l.unit || '').toLowerCase() === 'm' ? '' : ' ×'}` : '—'}</span>
                  <span style={{ flex: 1, minWidth: 180 }}>
                    {l.input_id != null
                      ? <Link to={`/stock/${l.input_id}`} style={{ color: TEXT, textDecoration: 'none', fontWeight: 600 }}>{l.item_name}</Link>
                      : <span style={{ fontWeight: 600 }}>{l.name}</span>}
                    <span style={{ display: 'block', fontSize: 12, color: l.why ? AMBER : MUTED }}>{l.why || l.basis}</span>
                  </span>
                  {l.input_id != null && (
                    <span style={{ fontSize: 12.5, color: l.level?.counted ? DIM : AMBER, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
                      title={l.left_out ? 'Not counted yet, so it does not limit Can make until it is. Made still takes it off.' : undefined}>
                      {l.level?.counted ? `${qty(l.level.total, { unit: l.unit })} on hand` : 'not counted yet: left out of can make'}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
          {item.packing.gaps.length > 0 && (
            <div style={{ fontSize: 12.5, color: AMBER, marginTop: 8, lineHeight: 1.6 }}>
              {item.packing.gaps.map((g, n) => <div key={n}>{g} Set it in Admin → Packaging, and it is counted here too.</div>)}
            </div>
          )}
        </div>
      )}

      {(item.used_in || []).length > 0 && (
        <div style={{ fontSize: 13, color: DIM, marginTop: 12, lineHeight: 1.6 }}>
          Goes into: {item.used_in.map((u, n) => (
            <span key={u.item_id}>{n ? ', ' : ''}<Link to={`/stock/${u.item_id}`} style={{ color: TEAL, textDecoration: 'none' }}>{u.name}</Link> ({u.from === 'packaging' ? amount(u.qty, item.unit) : perOne(u.qty)} each{u.from === 'packaging' ? ', from Packaging' : ''})</span>
          ))}
        </div>
      )}
    </section>
  );
}
